import { useEffect, useMemo, useRef, useState } from 'react';

import { ActionIcon, ButtonMantine, FileButton, Group, ImageCropperModal, Modal, Stack } from 'ui';
import { IconDelete } from 'ui/Icons';

export interface FileUploadProps {
  file?: File | null;
  setFile: (file: File | null) => void;
  existingSrc?: {
    url?: string;
    filename?: string;
  };
  setExistingSrc?: () => void;
  enableCropping?: boolean;
  dimensions?: [number, number];
  croppedImageFileName?: string | ((croppedImageFile: File) => string);
  isAddNewImage?: boolean;
  onSkip?: () => void;
}

const BannerImageFormActions = ({
  file,
  setFile,
  existingSrc,
  setExistingSrc,
  enableCropping = false,
  dimensions,
  croppedImageFileName,
  isAddNewImage,
  onSkip,
}: FileUploadProps) => {
  const resetRef = useRef<() => void>(null);
  const [preCroppedFile, setPreCroppedFile] = useState<File>();
  const [source, setSource] = useState<undefined | FileUploadProps['existingSrc']>();
  const isSrcOrFile = source ?? file;

  useEffect(() => {
    if (existingSrc) {
      setSource(existingSrc);
    }
  }, [existingSrc]);

  const imageFileName = useMemo(() => {
    if (!croppedImageFileName || !preCroppedFile) {
      return undefined;
    }

    if (typeof croppedImageFileName === 'function') {
      return croppedImageFileName(preCroppedFile);
    }

    return croppedImageFileName;
  }, [croppedImageFileName, preCroppedFile]);

  const clearFile = () => {
    resetRef.current?.();
  };

  const handleChangeImage = (file: File | null) => {
    if (file) {
      if (enableCropping) {
        setPreCroppedFile(file);
      } else {
        setFile(file);
      }
    }
  };

  const handleDeleteImage = () => {
    if (source) {
      setSource(undefined);
      setExistingSrc?.();
    } else {
      setFile(null);
    }
    clearFile();
  };

  return (
    <>
      {isAddNewImage ? (
        <Modal.AffixedFooter>
          <Stack gap="xs">
            <FileButton
              resetRef={resetRef}
              onChange={handleChangeImage}
              accept="image/png,image/jpeg"
            >
              {(props) => (
                <ButtonMantine fullWidth {...props}>
                  Add Image
                </ButtonMantine>
              )}
            </FileButton>
            {onSkip && (
              <ButtonMantine fullWidth variant="subtle" onClick={() => onSkip()}>
                Skip For Now
              </ButtonMantine>
            )}
          </Stack>
        </Modal.AffixedFooter>
      ) : (
        <Group justify="flex-end" gap="xs">
          <FileButton
            resetRef={resetRef}
            onChange={handleChangeImage}
            accept="image/png,image/jpeg"
          >
            {(props) => (
              <ButtonMantine size="sm" variant="outline" {...props}>
                Change Image
              </ButtonMantine>
            )}
          </FileButton>

          <ActionIcon
            size="sm"
            variant="outline"
            color="cerise"
            disabled={!isSrcOrFile}
            onClick={handleDeleteImage}
          >
            <IconDelete />
          </ActionIcon>
        </Group>
      )}
      <ImageCropperModal
        opened={enableCropping && !!preCroppedFile}
        dimensions={dimensions}
        onCropped={(croppedImage) => {
          setFile(croppedImage);
          setSource(undefined);
          setExistingSrc?.();
          // Closes the modal
          setPreCroppedFile(undefined);
        }}
        imageUrl={preCroppedFile ? URL.createObjectURL(preCroppedFile) : undefined}
        croppedImageFileName={imageFileName}
        onClose={() => {
          clearFile();
          setPreCroppedFile(undefined);
        }}
      />
    </>
  );
};

export { BannerImageFormActions };
